// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { RefObject } from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { Row, Col } from 'antd/lib/grid';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import notification from 'antd/lib/notification';
import Text from 'antd/lib/typography/Text';
import Alert from 'antd/lib/alert';
import Progress from 'antd/lib/progress';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { StorageLocation } from 'reducers';
import { getCore, LabelType, Storage } from 'cvat-core-wrapper';
import LabelsEditor from 'components/labels-editor/labels-editor';
import FileManagerComponent, { Files } from 'components/file-manager/file-manager';
import { RemoteFile } from 'components/file-manager/remote-browser';
import { getFileContentType, getContentTypeRemoteFile, getFileNameFromPath } from 'utils/files';

import BasicConfigurationForm, { BaseConfiguration } from './basic-configuration-form';
import ProjectSearchField from './project-search-field';
import ProjectSubsetField from './project-subset-field';
import MultiTasksProgress from './multi-task-progress';
import AdvancedConfigurationForm, {
    AdvancedConfiguration,
    SortingMethod,
    TranscriptionModel,
    TranscriptionDevice,
} from './advanced-configuration-form';
import Select from 'antd/lib/select';
import config from 'config';
import { idGenerator } from 'components/labels-editor/common';

const { Option } = Select;

type TabName = 'local' | 'share' | 'remote' | 'cloudStorage';
const core = getCore();

export interface CreateTaskData {
    projectId: number | null;
    basic: BaseConfiguration;
    subset: string;
    advanced: AdvancedConfiguration;
    labels: any[];
    jobAttrs: any[];
    files: Files;
    activeFileManagerTab: TabName;
    cloudStorageId: number | null;
}

enum SupportedShareTypes {
    IMAGE = 'image',
    DIR = 'DIR',
    VIDEO = 'video',
}

interface Props {
    onCreate: (data: CreateTaskData, onProgress?: (status: string, progress?: number) => void) => Promise<any>;
    projectId: number | null;
    installedGit: boolean;
    dumpers: [];
    many: boolean;
}

type TranscriptionQueueState = 'Queued' | 'Started' | 'Finished' | 'Failed';

interface TranscriptionStatusPayload {
    state: TranscriptionQueueState;
    message?: string;
    progress?: number;
}

type MultiTaskItem = CreateTaskData & {
    status: 'pending' | 'progress' | 'failed' | 'completed' | 'cancelled';
    transcriptionState?: TranscriptionQueueState;
    transcriptionMessage?: string;
    transcriptionProgress?: number;
};

type State = CreateTaskData & {
    multiTasks: MultiTaskItem[];
    uploadFileErrorMessage: string;
    loading: boolean;
    statusInProgressTask: string;
    singleTaskTranscriptionStatus: TranscriptionStatusPayload | null;
};

// attrs are organized hierarchically based on label names.
// For example 'person:Civilian:Driver' should inherit all attributes from 'person' and 'person:Civilian'
// This is only here.
// attrs are duplicated on the backend for each label for simplicity and flexibility.
const attrs = {
    phase1b: {
        job: [
            {
                name: 'phase1b.percentageCommunication',
                input_type: 'select',
                mutable: false,
                values: ['Less than 25%', '26-50%', '51-90%', 'more than 90%'],
            },
        ],
        // common person phase1b attrs
        person: [
            {
                name: 'demographics.race',
                input_type: 'multiselect',
                mutable: false,
                values: [
                    'White',
                    'Black or African American',
                    'Hispanic or Latino(a)',
                    'Native American',
                    'Asian',
                    'Middle Eastern',
                    'South Asian',
                    'Pacific Islander',
                    'None of these',
                    'Uncertain',
                ],
            },
            {
                name: 'demographics.age',
                input_type: 'select',
                mutable: false,
                values: ['<18', '18-30', '30-45', '45-65', '65+', 'Not sure'],
            },
            {
                name: 'demographics.sescar',
                input_type: 'select',
                mutable: false,
                values: ['Poor', 'Working Class', 'Middle Class', 'Wealthy', 'Not Obvious from Video'],
            },
            {
                name: 'demographics.sesclothing',
                input_type: 'select',
                mutable: false,
                values: ['Poor', 'Working Class', 'Middle Class', 'Wealthy', 'Not Obvious from Video'],
            },
            {
                name: 'demographics.gender',
                input_type: 'select',
                mutable: false,
                values: ['Male', 'Female', 'Trans Man', 'Trans Woman', 'Non-binary', 'Not Obvious from Video'],
            },
            {
                name: 'demographics.height',
                input_type: 'select',
                mutable: false,
                values: ['Short', 'Tall', 'Average', 'Not Obvious from Video'],
            },
            {
                name: 'demographics.foreignaccent',
                input_type: 'select',
                mutable: false,
                values: ['Yes', 'No', 'Uncertain'],
            },
            {
                name: 'demographics.regionalaccent',
                input_type: 'select',
                mutable: false,
                values: ['Yes', 'No', 'Uncertain'],
            },
            {
                name: 'demographics.lgbtq',
                input_type: 'select',
                mutable: false,
                values: ['Yes', 'No', 'Uncertain'],
            },
            {
                name: 'demographics.dui',
                input_type: 'select',
                mutable: false,
                values: ['Yes', 'No', 'Uncertain'],
            },
            {
                name: 'demographics.specialneeds',
                input_type: 'multiselect',
                mutable: false,
                nullable: true,
                values: [
                    'Deafness or difficulty hearing',
                    'Speech impairment or limited use of language',
                    'Blind or limited vision',
                    'Mental health condition',
                    'Intellectual or developmental disability',
                    'Disability related to hyperactivity or impulsive behavior',
                    'Other Disability',
                    'Uncertain',
                ],
            },
        ],
        // common person:Civilian phase1b attrs
        'person:Civilian': [
            {
                name: 'demographics.homelessness',
                input_type: 'select',
                mutable: false,
                values: ['Yes', 'No', 'Not Obvious from Video'],
            },
        ],
        'person:Civilian:Driver': [],
        'person:Civilian:Passenger': [],
        'person:Civilian:Bystander': [],
        // common person:Police phase1b attrs
        'person:Police': [],
        'person:Police:Primary': [],
        'person:Police:NonPrimary': [],
    },
    phase2a: {
        "job": [
            {
                "name": "phase2a.sidebar.highriskpullover",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.traffic",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.weather",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.background",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.background.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.backgroundtype",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.additionalunits",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.additionalarrive",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.additionalarrive.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.additionaltype",
                "input_type": "multiselect",
                "values": [
                    "Extra squad car",
                    "Fire",
                    "Ambulance/EMS",
                    "Helicopter",
                    "Non-officer support (e.g. social worker)",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person": [
            {
                "name": "phase2a.popup.0.fear",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.feartype",
                "input_type": "select",
                "values": [
                    "Fear of violence",
                    "Fear of Social Consequences"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.anger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.threatening",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.offensiveness",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.biasandhate",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.warmth",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.respectful",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2a.popup.0.doesnotcontribute",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2a.popup.0.emotionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.emotionother",
                "input_type": "multiselect",
                "values": [
                    "Happiness",
                    "Sadness",
                    "Surprise",
                    "Disgust",
                    "Anxiety",
                    "Confusion",
                    "Frustration",
                    "Relief",
                    "Contempt",
                    "Indifference",
                    "Gratitude",
                    "Curiosity",
                    "Impatience",
                    "Trust",
                    "Distrust",
                    "Embarrassment",
                    "Annoyance",
                    "N/A"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.fear",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.feartype",
                "input_type": "select",
                "values": [
                    "Fear of violence",
                    "Fear of Social Consequences"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.anger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.threatening",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.offensiveness",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.biasandhate",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.warmth",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.respectful",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2a.popup.1.doesnotcontribute",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2a.popup.1.emotionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.emotionother",
                "input_type": "multiselect",
                "values": [
                    "Happiness",
                    "Sadness",
                    "Surprise",
                    "Disgust",
                    "Anxiety",
                    "Confusion",
                    "Frustration",
                    "Relief",
                    "Contempt",
                    "Indifference",
                    "Gratitude",
                    "Curiosity",
                    "Impatience",
                    "Trust",
                    "Distrust",
                    "Embarrassment",
                    "Annoyance",
                    "N/A"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.fear",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.feartype",
                "input_type": "select",
                "values": [
                    "Fear of violence",
                    "Fear of Social Consequences"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.anger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.threatening",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.offensiveness",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.biasandhate",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.warmth",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.respectful",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2a.popup.2.doesnotcontribute",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2a.popup.2.emotionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.emotionother",
                "input_type": "multiselect",
                "values": [
                    "Happiness",
                    "Sadness",
                    "Surprise",
                    "Disgust",
                    "Anxiety",
                    "Confusion",
                    "Frustration",
                    "Relief",
                    "Contempt",
                    "Indifference",
                    "Gratitude",
                    "Curiosity",
                    "Impatience",
                    "Trust",
                    "Distrust",
                    "Embarrassment",
                    "Annoyance",
                    "N/A"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person:Civilian": [
            {
                "name": "phase2a.sidebar.clarity",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.clarity.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.apologyciv",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.apologyciv.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.thank",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.thank.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.hands",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.movements",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.movements.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.movementscommunication",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.movementscommunication.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.movementdirections",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.movementdirections.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.movementcomply",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.movementcomply.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.providedl",
                "input_type": "select",
                "values": [
                    "Yes and there appears to be no issue",
                    "Yes but there is an issue (e.g. expired photocopy etc.)",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.acknowledgefault",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.acknowledgefault.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.challengeappropriateness",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.challengeappropriateness.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.perspectiveonleadup",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.perspectiveonleadup.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.justification",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.justification.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.detailaskoff",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.detailaskoff.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.options",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.options.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.enforcementcomprehensioncheck",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "No enforcement action taken towards this person",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.enforcementcomprehensioncheck.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.detain",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.detain.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.detaintype",
                "input_type": "select",
                "values": [
                    "Curb",
                    "Patrol Car",
                    "Against a wall or fence",
                    "Not Obvious from Video",
                    "Uncertain",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.yell",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.yell.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.verbalattack",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.verbalattack.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.hatespeech",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.hatespeech.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.hatespeechopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.cellphone",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.cellphone.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.cellphoneuse",
                "input_type": "multiselect",
                "values": [
                    "Recording the Officer",
                    "Talking on the cellphone",
                    "Playing audio or video",
                    "To find and display documents",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.threatenviolence",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.threatenviolence.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.violencefollowup",
                "input_type": "select",
                "values": [
                    "Verbal Threat",
                    "Physical Intimidation",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.leavevehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.leavevehicle.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.leavevehicleagainst",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.force",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.force.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.forcetype",
                "input_type": "multiselect",
                "values": [
                    "Strike",
                    "Push or grab",
                    "Spit or bite",
                    "Use a weapon against",
                    "Attempt to strike",
                    "Attempt to push or grab",
                    "Attempt to spit or bite",
                    "Attempt to use a weapon against",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.paroleprobation",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.paroleask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.paroleask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.weaponinvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.weaponinvehicle.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.weapontype",
                "input_type": "multiselect",
                "values": [
                    "Gun",
                    "Knife",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.citation",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.citation.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.stateexactwarning/citation",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.stateexactwarning/citation.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.citation_open",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.impound",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.legalcounsel",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.legalcounsel.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.interrupt",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.interrupt.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.psychold",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.patdownask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.patdownask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.patdownpermissionfree",
                "input_type": "multiselect",
                "values": [
                    "Threat (e.g. \u201cIf you don\u2019t let me conduct a pat down I\u2019ll arrest you)",
                    "Promise (e.g. \u201cIf you let me conduct a pat down I\u2019ll let you go without a ticket.\u201d)",
                    "Demand (e.g. \u201cYou need to let me pat you down right now\u201d)",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.patdown",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.patdown.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.patdownpermission",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.patdownpermissionexplicit",
                "input_type": "select",
                "values": [
                    "Explicit (e.g. speaks the words \u201cyes\u201d or \u201cOK\u201d)",
                    "Implicit (e.g. nodding the head or spreading arms for pat down)",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandfoundpatdown",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandfoundpatdown.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.contrabandtypepatdown",
                "input_type": "multiselect",
                "values": [
                    "Gun(s)",
                    "Non-gun Weapon(s)",
                    "Drug or Drug Paraphernalia",
                    "Cash",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandseizedpatdown",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandseizedpatdown.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchperson",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchperson.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchpersonask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchpersonask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchpersonpermfree",
                "input_type": "multiselect",
                "values": [
                    "Threat (e.g. \u201cIf you don\u2019t let me search I\u2019ll arrest you)",
                    "Promise (e.g. \u201cIf you let me search I\u2019ll let you go without a ticket.\u201d)",
                    "Demand (e.g. \u201cYou need to let me search you right now.\u201d)",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchpersonpermission",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchpersonpermission.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchpersonpermissionexplicit",
                "input_type": "multiselect",
                "values": [
                    "Explicit (e.g. speaks the words \u201cyes\u201d or \u201cOK\u201d)",
                    "Implicit (e.g. nodding the head or spreading hands and legs)",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandfoundperson",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandfoundperson.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.contrabandtypeperson",
                "input_type": "multiselect",
                "values": [
                    "Gun(s)",
                    "Non-gun Weapon(s)",
                    "Drug or Drug Paraphernalia",
                    "Cash",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandseizedperson",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandseizedperson.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.weaponask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.weaponask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.stepoutask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.stepoutask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.suspicionindicated",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.suspicionindicated.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.suspicionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.freetoleave",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.outofplace",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.investigationask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.investigationask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.investigationopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.investigationhelp",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.investigationhelp.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.publicsafetyobject",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.publicsafetyobject.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.dephomesec",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.resist",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.resisttype",
                "input_type": "multiselect",
                "values": [
                    "Handcuff",
                    "Detention",
                    "Arrest",
                    "Step out Request",
                    "Not Obvious from Video",
                    "Uncertain",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.allegemisconduct",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.allegemisconduct.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.complaint",
                "input_type": "select",
                "values": [
                    "Yes to file a complaint",
                    "Yes to talk to supervisor",
                    "Yes to both file a complaint and talk to a supervisor",
                    "No",
                    "Not Obvious from Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.allegetype",
                "input_type": "multiselect",
                "values": [
                    "Profiling or bias on the basis of race - gender - or sexual orientation",
                    "Discourteous behavior",
                    "Use of force",
                    "Illegal Search",
                    "Illegal Seizure",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.allegetypeother",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.allegeresponse",
                "input_type": "multiselect",
                "values": [
                    "Call a supervisor",
                    "Assist the civilian in making a complaint",
                    "Provide information about how to file a complaint",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.handcuff",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.handcuff.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.arrest",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.arrest.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            }
        ],
        "person:Police": [
            {
                "name": "phase2a.popup.0.formality",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.0.empathy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.formality",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.1.empathy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.popup.2.empathy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.clarity2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.clarity2.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.greeting",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.introducename",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.introducedept",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.apologyoff",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.apologyoff.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.thank2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.thank2.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.compliment",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.compliment.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.anxiousapproach",
                "input_type": "select",
                "values": [
                    "Calm/neutral approach",
                    "Anxious/fearful approach",
                    "Not Obvious from Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.cartouch",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.prior",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.prior.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.priorlegalreason",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.priorsuspicion",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.priorsuspicionreason",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.cameratap",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.cameratap.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.cameramuffle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.cameramuffle.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.backup",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.backup.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.yell2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.yell2.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.verbalattack2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.verbalattack2.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.deflection",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.deflection.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.hatespeech2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.hatespeech2.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.hatespeechopen2",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.askregistration",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.askregistration.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.officerthreatenviolence",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.officerthreatenviolence.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.officerviolencefollowup",
                "input_type": "select",
                "values": [
                    "Verbal Threat",
                    "Physical Intimidation"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.weapondrawn",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.weapondrawn.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.weapontype2",
                "input_type": "multiselect",
                "values": [
                    "Gun",
                    "Taser",
                    "Baton",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.interrupt2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.interrupt2.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.endpositive",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.endpositive.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.concernforsafety",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.officerforce",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.officerforce.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.officerforcetype",
                "input_type": "multiselect",
                "values": [
                    "Strike",
                    "Push or grab",
                    "Spit or bite",
                    "Use a weapon against",
                    "Attempt to strike",
                    "Attempt to push or grab",
                    "Attempt to spit or bite",
                    "Attempt to use a weapon against",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person:Civilian:Driver": [
            {
                "name": "phase2a.sidebar.handswheel",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.pullsafely",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.provideregistration",
                "input_type": "select",
                "values": [
                    "Yes and there appears to be no issue",
                    "Yes but expired or other issue",
                    "No",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.stopcomprehensioncheck",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.stopcomprehensioncheck.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchvehicle.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchpermfree",
                "input_type": "multiselect",
                "values": [
                    "Threat (e.g. \u201cIf you don\u2019t let me search I\u2019ll arrest you)",
                    "Promise (e.g. \u201cIf you let me search I\u2019ll let you go without a ticket.\u201d)",
                    "Demand (e.g. \u201cYou need to let me search your car right now.\u201d)",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchpermission",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.searchpermission.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.searchpermissionexplicit",
                "input_type": "multiselect",
                "values": [
                    "Explicit (e.g. speaks the words \u201cyes\u201d or \u201cOK\u201d)",
                    "Implicit (e.g. nodding the head or handing the officer the key to the trunk)",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandfoundvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandfoundvehicle.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.contrabandtypevehicle",
                "input_type": "multiselect",
                "values": [
                    "Gun(s)",
                    "Non-gun Weapon(s)",
                    "Drug or Drug Paraphernalia",
                    "Cash",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandseizedvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.contrabandseizedvehicle.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasonask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasonask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasongiven",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasongiven.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasontype",
                "input_type": "multiselect",
                "values": [
                    "Speeding",
                    "Red light or stop sign violation",
                    "Cell phone use",
                    "Other moving violation",
                    "Equipment violation (e.g.window tint)",
                    "Other non-moving violation",
                    "Reasonable suspicion of criminal activity",
                    "None of these"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.ifspeedinga",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.ifspeedingb",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.ifspeedingc",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasonspecific",
                "input_type": "select",
                "values": [
                    "Yes — specific reason",
                    "No — vague",
                    "No — circular",
                    "No — nonsensical",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasonspecific.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasoncivilianspecific",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasoncivilianspecific.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasonofficerintent",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasonofficerintent.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasoncare",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasoncare.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasoncarequalifier",
                "input_type": "select",
                "values": [
                    "Conditional — it depends on the civilian complying (e.g. “If your license comes back clean I’ll get you on your way safe.”)",
                    "No stated intent — a well-wish with no intent behind it (e.g. “Drive safe.”)",
                    "Neither applies",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasoncarequalifier.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.reasonprompt",
                "input_type": "multiselect",
                "values": [
                    "Nothing — the officer gave it unprompted",
                    "A civilian statement or action that was neutral - compliant - or cooperative",
                    "A civilian statement or action that was non-compliant - aggressive - or oppositional",
                    "A question from the civilian",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.reasonprompt.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.duiask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2a.sidebar.duiask.time",
                "input_type": "timelist",
                "values": [],
                "mutable": false
            },
            {
                "name": "phase2a.sidebar.sobrietytest",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        // these empty things need to be here
        'person:Police:Primary': [],
        'person:Police:NonPrimary': [],
        'person:Civilian:Passenger': [],
        'person:Civilian:Bystander': [],
    },
    phase2b: {
        job: [
            {
                "name": "phase2b.popup.0.overallsummary",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase2b.popup.0.subjectivesummary",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2b.popup.0.objectivesummary",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2b.popup.0.legitimacy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2b.popup.0.legitimacyopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
        ],
        person: [
            {
                "name": "phase2b.popup.recurring.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true,
            },
            {
                "name": "phase2b.popup.recurring.respect",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            // {
            //     "name": "phase2b.popup.0.doesnotappear",
            //     "input_type": "checkbox",
            //     "values": [
            //         "false"
            //     ],
            //     "mutable": false,
            //     "nullable": true,
            // },
            {
                "name": "phase2b.popup.0.overallrespect",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2b.popup.0.overallrespectopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2b.popup.0.danger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase2b.popup.0.metapdanger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        'person:Police': [],
        'person:Civilian': [],
        'person:Police:Primary': [],
        'person:Police:NonPrimary': [],
        'person:Civilian:Driver': [],
        'person:Civilian:Passenger': [],
        'person:Civilian:Bystander': [],

    },
    phase3a: {
        "job": [
            {
                "name": "phase3a.sidebar.highriskpullover",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.traffic",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.weather",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.background",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.backgroundtype",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.additionalunits",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.additionalarrive",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.additionaltype",
                "input_type": "multiselect",
                "values": [
                    "Extra squad car",
                    "Fire",
                    "Ambulance/EMS",
                    "Helicopter",
                    "Non-officer support (e.g. social worker)",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person": [
            {
                "name": "phase3a.popup.0.fear",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.feartype",
                "input_type": "select",
                "values": [
                    "Fear of violence",
                    "Fear of Social Consequences"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.anger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.threatening",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.offensiveness",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.biasandhate",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.warmth",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.respectful",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "False"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase3a.popup.0.doesnotcontribute",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase3a.popup.0.emotionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.emotionother",
                "input_type": "multiselect",
                "values": [
                    "Happiness",
                    "Sadness",
                    "Surprise",
                    "Disgust",
                    "Anxiety",
                    "Confusion",
                    "Frustration",
                    "Relief",
                    "Contempt",
                    "Indifference",
                    "Gratitude",
                    "Curiosity",
                    "Impatience",
                    "Trust",
                    "Distrust",
                    "Embarrassment",
                    "Annoyance",
                    "N/A"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "False"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase3a.popup.1.doesnotcontribute",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase3a.popup.1.fear",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.feartype",
                "input_type": "select",
                "values": [
                    "Fear of violence",
                    "Fear of Social Consequences"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.anger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.threatening",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.offensiveness",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.biasandhate",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.warmth",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.respectful",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.emotionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.emotionother",
                "input_type": "multiselect",
                "values": [
                    "Happiness",
                    "Sadness",
                    "Surprise",
                    "Disgust",
                    "Anxiety",
                    "Confusion",
                    "Frustration",
                    "Relief",
                    "Contempt",
                    "Indifference",
                    "Gratitude",
                    "Curiosity",
                    "Impatience",
                    "Trust",
                    "Distrust",
                    "Embarrassment",
                    "Annoyance",
                    "N/A"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.fear",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.feartype",
                "input_type": "select",
                "values": [
                    "Fear of violence",
                    "Fear of Social Consequences"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.anger",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.threatening",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.offensiveness",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.biasandhate",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.warmth",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.respectful",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.doesnotappear",
                "input_type": "checkbox",
                "values": [
                    "False"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase3a.popup.2.doesnotcontribute",
                "input_type": "checkbox",
                "values": [
                    "false"
                ],
                "mutable": false,
                "nullable": true
            },
            {
                "name": "phase3a.popup.2.emotionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.emotionother",
                "input_type": "multiselect",
                "values": [
                    "Happiness",
                    "Sadness",
                    "Surprise",
                    "Disgust",
                    "Anxiety",
                    "Confusion",
                    "Frustration",
                    "Relief",
                    "Contempt",
                    "Indifference",
                    "Gratitude",
                    "Curiosity",
                    "Impatience",
                    "Trust",
                    "Distrust",
                    "Embarrassment",
                    "Annoyance",
                    "N/A"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person:Police": [
            {
                "name": "phase3a.popup.0.formality",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.0.empathy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.formality",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.1.empathy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.formality",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.popup.2.empathy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.clarity2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.greeting",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.introducename",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.introducedept",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.apologyoff",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.thank2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.compliment",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.anxiousapproach",
                "input_type": "select",
                "values": [
                    "Calm/neutral approach",
                    "Anxious/fearful approach",
                    "Not Obvious from Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.cartouch",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.prior",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.priorlegalreason",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.priorsuspicion",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.priorsuspicionreason",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.cameratap",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.cameramuffle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.backup",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.yell2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.verbalattack2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.deflection",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.hatespeech2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.hatespeechopen2",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.officerthreatenviolence",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.officerviolencefollowup",
                "input_type": "select",
                "values": [
                    "Verbal Threat",
                    "Physical Intimidation"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.weapondrawn",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.weapontype2",
                "input_type": "multiselect",
                "values": [
                    "Gun",
                    "Taser",
                    "Baton",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.interrupt2",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.endpositive",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.concernforsafety",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.officerforce",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.officerforcetype",
                "input_type": "multiselect",
                "values": [
                    "Strike",
                    "Push or grab",
                    "Spit or bite",
                    "Use a weapon against",
                    "Attempt to strike",
                    "Attempt to push or grab",
                    "Attempt to spit or bite",
                    "Attempt to use a weapon against",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person:Civilian": [
            {
                "name": "phase3a.sidebar.clarity",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.apologyciv",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.thank",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.hands",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.movements",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.movementscommunication",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.movementdirections",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.movementcomply",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.providedl",
                "input_type": "select",
                "values": [
                    "Yes and there appears to be no issue",
                    "Yes but there is an issue (e.g. expired photocopy etc.)",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.acknowledgefault",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.challengeappropriateness",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.perspectiveonleadup",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.justification",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.detailaskoff",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.options",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.enforcementcomprehensioncheck",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "No enforcement action taken towards this person",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.detain",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.detaintype",
                "input_type": "select",
                "values": [
                    "Curb",
                    "Patrol Car",
                    "Against a wall or fence",
                    "Not Obvious from Video",
                    "Uncertain",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.yell",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.verbalattack",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.hatespeech",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.hatespeechopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.cellphone",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.cellphoneuse",
                "input_type": "multiselect",
                "values": [
                    "Recording the Officer",
                    "Talking on the cellphone",
                    "Playing audio or video",
                    "To find and display documents",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.threatenviolence",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.violencefollowup",
                "input_type": "select",
                "values": [
                    "Verbal Threat",
                    "Physical Intimidation",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.leavevehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.leavevehicleagainst",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.force",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.forcetype",
                "input_type": "multiselect",
                "values": [
                    "Strike",
                    "Push or grab",
                    "Spit or bite",
                    "Use a weapon against",
                    "Attempt to strike",
                    "Attempt to push or grab",
                    "Attempt to spit or bite",
                    "Attempt to use a weapon against",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.paroleprobation",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.paroleask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.weaponinvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.weapontype",
                "input_type": "multiselect",
                "values": [
                    "Gun",
                    "Knife",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.citation",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.stateexactwarning/citation",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.citation_open",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.legalcounsel",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.interrupt",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.psychold",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.patdownask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.patdownpermissionfree",
                "input_type": "multiselect",
                "values": [
                    "Threat (e.g. “If you don’t let me conduct a pat down I’ll arrest you)",
                    "Promise (e.g. “If you let me conduct a pat down I’ll let you go without a ticket.”)",
                    "Demand (e.g. “You need to let me pat you down right now”)",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.patdown",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.patdownpermission",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.patdownpermissionexplicit",
                "input_type": "select",
                "values": [
                    "Explicit (e.g. speaks the words “yes” or “OK”)",
                    "Implicit (e.g. nodding the head or spreading arms for pat down)",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandfoundpatdown",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandtypepatdown",
                "input_type": "multiselect",
                "values": [
                    "Gun(s)",
                    "Non-gun Weapon(s)",
                    "Drug or Drug Paraphernalia",
                    "Cash",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandseizedpatdown",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchperson",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpersonask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpersonpermfree",
                "input_type": "multiselect",
                "values": [
                    "Threat (e.g. “If you don’t let me search I’ll arrest you)",
                    "Promise (e.g. “If you let me search I’ll let you go without a ticket.”)",
                    "Demand (e.g. “You need to let me search you right now.”)",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpersonpermission",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpersonpermissionexplicit",
                "input_type": "multiselect",
                "values": [
                    "Explicit (e.g. speaks the words “yes” or “OK”)",
                    "Implicit (e.g. nodding the head or spreading hands and legs)",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandfoundperson",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandtypeperson",
                "input_type": "multiselect",
                "values": [
                    "Gun(s)",
                    "Non-gun Weapon(s)",
                    "Drug or Drug Paraphernalia",
                    "Cash",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandseizedperson",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.weaponask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.stepoutask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.suspicionindicated",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.suspicionopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.freetoleave",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.outofplace",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.investigationask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.investigationopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.investigationhelp",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.publicsafetyobject",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.dephomesec",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.resist",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.resisttype",
                "input_type": "multiselect",
                "values": [
                    "Handcuff",
                    "Detention",
                    "Arrest",
                    "Step out Request",
                    "Not Obvious from Video",
                    "Uncertain",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.allegemisconduct",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.complaint",
                "input_type": "select",
                "values": [
                    "Yes to file a complaint",
                    "Yes to talk to supervisor",
                    "Yes to both file a complaint and talk to a supervisor",
                    "No",
                    "Not Obvious from Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.allegetype",
                "input_type": "multiselect",
                "values": [
                    "Profiling or bias on the basis of race - gender - or sexual orientation",
                    "Discourteous behavior",
                    "Use of force",
                    "Illegal Search",
                    "Illegal Seizure",
                    "Other"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.allegetypeother",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.allegeresponse",
                "input_type": "multiselect",
                "values": [
                    "Call a supervisor",
                    "Assist the civilian in making a complaint",
                    "Provide information about how to file a complaint",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.handcuff",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.arrest",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        "person:Civilian:Driver": [
            {
                "name": "phase3a.sidebar.handswheel",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.pullsafely",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.provideregistration",
                "input_type": "select",
                "values": [
                    "Yes and there appears to be no issue",
                    "Yes but expired or other issue",
                    "No",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.stopcomprehensioncheck",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.impound",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpermfree",
                "input_type": "multiselect",
                "values": [
                    "Threat (e.g. “If you don’t let me search I’ll arrest you)",
                    "Promise (e.g. “If you let me search I’ll let you go without a ticket.”)",
                    "Demand (e.g. “You need to let me search your car right now.”)",
                    "None of the above"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpermission",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.searchpermissionexplicit",
                "input_type": "multiselect",
                "values": [
                    "Explicit (e.g. speaks the words “yes” or “OK”)",
                    "Implicit (e.g. nodding the head or handing the officer the key to the trunk)",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandfoundvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandtypevehicle",
                "input_type": "multiselect",
                "values": [
                    "Gun(s)",
                    "Non-gun Weapon(s)",
                    "Drug or Drug Paraphernalia",
                    "Cash",
                    "Not obvious from video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.contrabandseizedvehicle",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasonask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasongiven",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasontype",
                "input_type": "multiselect",
                "values": [
                    "Speeding",
                    "Red light or stop sign violation",
                    "Cell phone use",
                    "Other moving violation",
                    "Equipment violation (e.g.window tint)",
                    "Other non-moving violation",
                    "Reasonable suspicion of criminal activity",
                    "None of these"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.ifspeedinga",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.ifspeedingb",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.ifspeedingc",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasonspecific",
                "input_type": "select",
                "values": [
                    "Yes — specific reason",
                    "No — vague",
                    "No — circular",
                    "No — nonsensical",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasoncivilianspecific",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasonofficerintent",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasoncare",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasoncarequalifier",
                "input_type": "select",
                "values": [
                    "Conditional — it depends on the civilian complying (e.g. “If your license comes back clean I’ll get you on your way safe.”)",
                    "No stated intent — a well-wish with no intent behind it (e.g. “Drive safe.”)",
                    "Neither applies",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.reasonprompt",
                "input_type": "multiselect",
                "values": [
                    "Nothing — the officer gave it unprompted",
                    "A civilian statement or action that was neutral - compliant - or cooperative",
                    "A civilian statement or action that was non-compliant - aggressive - or oppositional",
                    "A question from the civilian",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.duiask",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3a.sidebar.sobrietytest",
                "input_type": "select",
                "values": [
                    "Yes",
                    "No",
                    "Not Obvious From Video",
                    "Uncertain"
                ],
                "mutable": false,
                "nullable": false
            }
        ],
        'person:Police:Primary': [],
        'person:Police:NonPrimary': [],
        'person:Civilian:Passenger': [],
        'person:Civilian:Bystander': [],
    },
    phase3b: {
        person: [
            {
                "name": "phase3b.sidebar.respect",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3b.sidebar.respectopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
        ],
        'person:Police': [
            {
                name: 'phase3b.sidebar.respectmultiselectpolice',
                input_type: 'multiselect',
                mutable: false,
                nullable: false,
                values: [
                    "Attentive",
                    "Intrusive",
                    "Polite",
                    "Explanatory",
                    "Empathetic",
                    "Friendly",
                    "Apologetic",
                    "Cooperative",
                    "Lenient",
                    "Professional",
                    "Silent",
                    "Observant",
                    "Indifferent",
                    "Intimidating",
                    "Annoyed"
                ]
            }
        ],
        'person:Civilian': [
            {
                name: 'phase3b.sidebar.respectmultiselectcivilian',
                input_type: 'multiselect',
                mutable: false,
                nullable: false,
                values: [
                    "Apologetic",
                    "Honest",
                    "Understanding",
                    "Communicative",
                    "Polite",
                    "Compliant",
                    "Friendly",
                    "Grateful",
                    "Confused",
                    "Brief",
                    "Silent",
                    "Passive",
                    "Detached",
                    "Frustrated",
                    "Defiant",
                    "Aggressive"
                ],
            }
        ],
        job: [
            {
                "name": "phase3b.sidebar.overallobjectivesummary",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3b.sidebar.overallsubjectivesummary",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3b.sidebar.legitimacy",
                "input_type": "number",
                "values": [
                    "1",
                    "5",
                    "1"
                ],
                "mutable": false,
                "nullable": false
            },
            {
                "name": "phase3b.sidebar.legitimacyopen",
                "input_type": "text",
                "values": [
                    ""
                ],
                "mutable": false,
                "nullable": false
            },
        ],
        'person:Police:Primary': [],
        'person:Police:NonPrimary': [],
        'person:Civilian:Driver': [],
        'person:Civilian:Passenger': [],
        'person:Civilian:Bystander': [],
    }
};

const defaultState: State = {
    projectId: null,
    basic: {
        name: '',
    },
    subset: '',
    advanced: {
        lfs: false,
        useZipChunks: false,
        initialJobCount: 1,
        transcriptionModel: TranscriptionModel.WHISPERX,
        transcriptionDevice: TranscriptionDevice.CPU,
        useCache: true,
        sortingMethod: SortingMethod.LEXICOGRAPHICAL,
        sourceStorage: {
            location: StorageLocation.LOCAL,
            cloudStorageId: undefined,
        },
        targetStorage: {
            location: StorageLocation.LOCAL,
            cloudStorageId: undefined,
        },
        useProjectSourceStorage: true,
        useProjectTargetStorage: true,
    },
    labels: [
        {
            id: idGenerator(),
            name: 'person:Police:Primary',
            type: LabelType.ANY,
            color: '#4aa7ff',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
                ...attrs.phase1b.person.map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2a.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Police:Primary'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Police:Primary'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3a.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Police:Primary'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Police:Primary'].map((attr) => JSON.parse(JSON.stringify(attr))),
            ],
        },
        {
            id: idGenerator(),
            name: 'person:Police:NonPrimary',
            type: LabelType.ANY,
            color: '#4aa7ff',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
                ...attrs.phase1b.person.map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2a.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Police:NonPrimary'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Police:NonPrimary'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3a.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Police:NonPrimary'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Police'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Police:NonPrimary'].map((attr) => JSON.parse(JSON.stringify(attr))),
            ],
        },
        {
            id: idGenerator(),
            name: 'person:Civilian:Driver',
            type: LabelType.ANY,
            color: '#f2d25c',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
                ...attrs.phase1b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase1b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2a['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Civilian:Driver'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2b['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Civilian:Driver'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3a['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Civilian:Driver'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3b['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Civilian:Driver'].map((attr) => JSON.parse(JSON.stringify(attr))),
            ],
        },
        {
            id: idGenerator(),
            name: 'person:Civilian:Passenger',
            type: LabelType.ANY,
            color: '#f2d25c',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
                ...attrs.phase1b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase1b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2a['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Civilian:Passenger'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2b['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Civilian:Passenger'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3a['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Civilian:Passenger'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3b['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Civilian:Passenger'].map((attr) => JSON.parse(JSON.stringify(attr))),
            ],
        },
        {
            id: idGenerator(),
            name: 'person:Civilian:Bystander',
            type: LabelType.ANY,
            color: '#f2d25c',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
                ...attrs.phase1b.person.map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase1b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2a['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2a['person:Civilian:Bystander'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase2b['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase2b['person:Civilian:Bystander'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3a['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3a['person:Civilian:Bystander'].map((attr) => JSON.parse(JSON.stringify(attr))),

                ...attrs.phase3b['person'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Civilian'].map((attr) => JSON.parse(JSON.stringify(attr))),
                ...attrs.phase3b['person:Civilian:Bystander'].map((attr) => JSON.parse(JSON.stringify(attr))),
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Police',
            type: LabelType.ANY,
            color: '#374f6b',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Police:Sedan',
            type: LabelType.ANY,
            color: '#374f6b',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Police:SUV',
            type: LabelType.ANY,
            color: '#374f6b',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Police:Motorcycle',
            type: LabelType.ANY,
            color: '#374f6b',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Civilian',
            type: LabelType.ANY,
            color: '#008000',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Civilian:Sedan',
            type: LabelType.ANY,
            color: '#008000',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Civilian:SUV',
            type: LabelType.ANY,
            color: '#008000',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Civilian:Motorcylce',
            type: LabelType.ANY,
            color: '#008000',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
        {
            id: idGenerator(),
            name: 'car:Civilian:Pickup_Truck',
            type: LabelType.ANY,
            color: '#008000',
            attributes: [
                {
                    name: 'identifier',
                    input_type: 'number',
                    mutable: false,
                    values: ['1', '20', '1'],
                },
            ],
        },
    ],
    files: {
        local: [],
        share: [],
        remote: [],
        cloudStorage: [],
    },
    activeFileManagerTab: 'local',
    cloudStorageId: null,
    multiTasks: [],
    uploadFileErrorMessage: '',
    loading: false,
    statusInProgressTask: '',
    singleTaskTranscriptionStatus: null,
    jobAttrs: [...attrs.phase1b.job, ...attrs.phase2a.job, ...attrs.phase2b.job, ...attrs.phase3a.job, ...attrs.phase3b.job],
};

const UploadFileErrorMessages = {
    one: 'It can not be processed. You can upload an archive with images, a video, a pdf file or multiple images',
    multi: 'It can not be processed. You can upload one or more videos',
};

function validateRemoteFiles(remoteFiles: RemoteFile[], many: boolean): string {
    let uploadFileErrorMessage = '';
    let filteredFiles = remoteFiles;
    const regFiles = remoteFiles.filter((file) => file.type === 'REG');
    const excludedManifests = regFiles.filter((file) => !file.key.endsWith('.jsonl'));
    if (!many && excludedManifests.length > 1) {
        uploadFileErrorMessage = excludedManifests.every((it) => it.mimeType === SupportedShareTypes.IMAGE)
            ? ''
            : UploadFileErrorMessages.one;
    } else if (many) {
        filteredFiles = filteredFiles.filter((it) => it.mimeType === SupportedShareTypes.VIDEO);
        // something is selected and no one video
        // or something except of videos selected (excluding directories)
        uploadFileErrorMessage =
            remoteFiles.length && (!filteredFiles.length || filteredFiles.length !== regFiles.length)
                ? UploadFileErrorMessages.multi
                : '';
    }
    return uploadFileErrorMessage;
}

function filterFiles(remoteFiles: RemoteFile[], many: boolean): RemoteFile[] {
    if (many) {
        return remoteFiles.filter((file: RemoteFile) => file.mimeType === 'video');
    }

    return remoteFiles;
}

class CreateTaskContent extends React.PureComponent<Props & RouteComponentProps, State> {
    private basicConfigurationComponent: RefObject<BasicConfigurationForm>;
    private advancedConfigurationComponent: RefObject<AdvancedConfigurationForm>;
    private fileManagerComponent: any;
    private transcriptionPollTimeouts: Map<string, number>;
    private isUnmounted: boolean;

    public constructor(props: Props & RouteComponentProps) {
        super(props);
        this.state = { ...defaultState };
        this.basicConfigurationComponent = React.createRef<BasicConfigurationForm>();
        this.advancedConfigurationComponent = React.createRef<AdvancedConfigurationForm>();
        this.transcriptionPollTimeouts = new Map<string, number>();
        this.isUnmounted = false;
    }

    public componentDidMount(): void {
        const { projectId } = this.props;

        if (projectId) {
            this.handleProjectIdChange(projectId);
        }

        this.focusToForm();
    }

    public componentWillUnmount(): void {
        this.isUnmounted = true;
        this.transcriptionPollTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
        this.transcriptionPollTimeouts.clear();
    }

    private handleChangeStorageLocation(field: 'sourceStorage' | 'targetStorage', value: StorageLocation): void {
        this.setState((state) => ({
            advanced: {
                ...state.advanced,
                [field]: {
                    location: value,
                },
            },
        }));
    }

    private resetState = (): void => {
        this.basicConfigurationComponent.current?.resetFields();
        this.advancedConfigurationComponent.current?.resetFields();

        this.fileManagerComponent.reset();

        this.setState((state) => ({
            ...defaultState,
            projectId: state.projectId,
        }));
    };

    private validateLabelsOrProject = (): boolean => {
        const { projectId, labels } = this.state;
        return !!labels.length || !!projectId;
    };

    private validateFiles = (): boolean => {
        const { activeFileManagerTab, files } = this.state;

        if (activeFileManagerTab === 'cloudStorage') {
            this.setState({
                cloudStorageId: this.fileManagerComponent.getCloudStorageId(),
            });
        }
        const totalLen = Object.keys(files).reduce((acc, key: string) => acc + files[key as TabName].length, 0);

        return !!totalLen;
    };

    private startLoading = (): void => {
        this.setState({
            loading: true,
        });
    };

    private stopLoading = (): void => {
        this.setState({
            loading: false,
        });
    };

    private changeStatusInProgressTask = (status: string): void => {
        this.setState({
            statusInProgressTask: status,
        });
    };

    private getTranscriptionNotificationKey(taskID: number): string {
        return `cvat-transcription-status-${taskID}`;
    }

    private clearTranscriptionPoll(taskID: number): void {
        const key = this.getTranscriptionNotificationKey(taskID);
        const timeoutID = this.transcriptionPollTimeouts.get(key);
        if (typeof timeoutID !== 'undefined') {
            window.clearTimeout(timeoutID);
            this.transcriptionPollTimeouts.delete(key);
        }
    }

    private normalizeTranscriptionStatus(status: any): TranscriptionStatusPayload {
        const state = ['Queued', 'Started', 'Finished', 'Failed'].includes(status?.state) ?
            status.state as TranscriptionQueueState :
            'Started';
        const message = typeof status?.message === 'string' ? status.message : '';
        const progress = typeof status?.progress === 'number' ? status.progress : 0;
        return { state, message, progress };
    }

    private buildTranscriptionStatusMessage(
        status: TranscriptionStatusPayload,
        taskName: string,
    ): string {
        const progress = status.progress && status.progress > 0 ?
            ` (${Math.round(status.progress)}%)` :
            '';
        const messageSuffix = status.message ? ` ${status.message}` : '';

        if (status.state === 'Queued') {
            return `Transcription queued for "${taskName}".${messageSuffix}`;
        }
        if (status.state === 'Started') {
            return `Transcription in progress for "${taskName}"${progress}.${messageSuffix}`;
        }
        if (status.state === 'Failed') {
            return `Transcription failed for "${taskName}".${messageSuffix}`;
        }

        return `Transcription completed for "${taskName}".`;
    }

    private updateMultiTaskTranscriptionStatus(
        index: number,
        status: TranscriptionStatusPayload,
    ): void {
        this.setState((prevState) => {
            if (!prevState.multiTasks[index]) {
                return null;
            }

            const updatedTask = {
                ...prevState.multiTasks[index],
                transcriptionState: status.state,
                transcriptionMessage: status.message || '',
                transcriptionProgress: status.progress || 0,
            };

            return {
                multiTasks: [
                    ...prevState.multiTasks.slice(0, index),
                    updatedTask,
                    ...prevState.multiTasks.slice(index + 1),
                ],
            };
        });
    }

    private monitorTaskTranscriptionStatus(
        taskID: number,
        taskName: string,
        {
            updateSingleStatus = false,
            notify = false,
            multiTaskIndex,
        }: {
            updateSingleStatus?: boolean;
            notify?: boolean;
            multiTaskIndex?: number;
        } = {},
    ): void {
        const key = this.getTranscriptionNotificationKey(taskID);
        const queuedStatus: TranscriptionStatusPayload = {
            state: 'Queued',
            message: 'Transcription job is queued to start.',
            progress: 0,
        };

        if (updateSingleStatus) {
            this.setState({
                statusInProgressTask: this.buildTranscriptionStatusMessage(queuedStatus, taskName),
                singleTaskTranscriptionStatus: queuedStatus,
            });
        }

        if (typeof multiTaskIndex === 'number') {
            this.updateMultiTaskTranscriptionStatus(multiTaskIndex, queuedStatus);
        }

        const poll = async (): Promise<void> => {
            if (this.isUnmounted) {
                return;
            }

            let status: TranscriptionStatusPayload;
            try {
                status = this.normalizeTranscriptionStatus(await core.tasks.transcriptionStatus(taskID));
            } catch (error) {
                status = {
                    state: 'Started',
                    message: 'Unable to fetch transcription status. Retrying...',
                    progress: 0,
                };
            }

            const text = this.buildTranscriptionStatusMessage(status, taskName);

            if (updateSingleStatus) {
                this.setState({
                    statusInProgressTask: text,
                    singleTaskTranscriptionStatus: status,
                });
            }

            if (typeof multiTaskIndex === 'number') {
                this.updateMultiTaskTranscriptionStatus(multiTaskIndex, status);
            }

            if (notify) {
                const notifyFn = status.state === 'Failed' ? notification.error : notification.info;
                notifyFn({
                    key,
                    message: `Task "${taskName}" transcription`,
                    description: text,
                    duration: status.state === 'Queued' || status.state === 'Started' ? 0 : 6,
                });
            }

            if (status.state === 'Finished' || status.state === 'Failed') {
                this.clearTranscriptionPoll(taskID);
                return;
            }

            const timeoutID = window.setTimeout(() => {
                poll().catch(() => {
                    // noop
                });
            }, 2000);
            this.transcriptionPollTimeouts.set(key, timeoutID);
        };

        poll().catch(() => {
            // noop
        });
    }

    private handleProjectIdChange = (value: null | number): void => {
        const { projectId, subset } = this.state;

        this.setState((state) => ({
            projectId: value,
            subset: value && value === projectId ? subset : '',
            labels: value ? [] : state.labels,
        }));
    };

    private handleChangeBasicConfiguration = (values: BaseConfiguration): void => {
        this.setState({
            basic: { ...values },
        });
    };

    private handleSubmitAdvancedConfiguration = (values: AdvancedConfiguration): void => {
        this.setState({
            advanced: { ...values },
        });
    };

    private handleTaskSubsetChange = (value: string): void => {
        this.setState({
            subset: value,
        });
    };

    private changeFileManagerTab = (value: TabName): void => {
        this.setState({
            activeFileManagerTab: value,
        });
    };

    private handleUseProjectSourceStorageChange = (value: boolean): void => {
        this.setState((state) => ({
            advanced: {
                ...state.advanced,
                useProjectSourceStorage: value,
            },
        }));
    };

    private handleUseProjectTargetStorageChange = (value: boolean): void => {
        this.setState((state) => ({
            advanced: {
                ...state.advanced,
                useProjectTargetStorage: value,
            },
        }));
    };

    private focusToForm = (): void => {
        this.basicConfigurationComponent.current?.focus();
    };

    private handleUploadLocalFiles = (uploadedFiles: File[]): void => {
        const { many } = this.props;
        const { files } = this.state;

        let uploadFileErrorMessage = '';

        const excludedManifests = uploadedFiles.filter((x: File) => !x.name.endsWith('.jsonl'));
        if (!many && excludedManifests.length > 1) {
            uploadFileErrorMessage = excludedManifests.every(
                (it) => getFileContentType(it) === SupportedShareTypes.IMAGE,
            )
                ? ''
                : UploadFileErrorMessages.one;
        } else if (many) {
            uploadFileErrorMessage = excludedManifests.every(
                (it) => getFileContentType(it) === SupportedShareTypes.VIDEO,
            )
                ? ''
                : UploadFileErrorMessages.multi;
        }

        this.setState({
            uploadFileErrorMessage,
        });

        if (!uploadFileErrorMessage) {
            this.setState({
                files: {
                    ...files,
                    local: uploadedFiles,
                },
            });
        }
    };

    private handleUploadRemoteFiles = (urls: string[]): void => {
        const { many } = this.props;

        const { files } = this.state;
        const { length } = urls;

        let uploadFileErrorMessage = '';

        try {
            if (!many && length > 1) {
                let index = 0;
                while (index < length) {
                    const isImageFile = getContentTypeRemoteFile(urls[index]) === 'image';
                    if (!isImageFile) {
                        uploadFileErrorMessage = UploadFileErrorMessages.one;
                        break;
                    }
                    index++;
                }
            } else if (many) {
                let index = 0;
                while (index < length) {
                    const isVideoFile = getContentTypeRemoteFile(urls[index]) === 'video';
                    if (!isVideoFile) {
                        uploadFileErrorMessage = UploadFileErrorMessages.multi;
                        break;
                    }
                    index++;
                }
            }
        } catch (err) {
            uploadFileErrorMessage = `We can't process it. ${err}`;
        }

        this.setState({
            uploadFileErrorMessage,
        });

        if (!uploadFileErrorMessage) {
            this.setState({
                files: {
                    ...files,
                    remote: urls,
                },
            });
        }
    };

    private handleUploadShareFiles = (shareFiles: RemoteFile[]): void => {
        const { files } = this.state;
        const { many } = this.props;
        const uploadFileErrorMessage = validateRemoteFiles(shareFiles, many);
        const filteredFiles = filterFiles(shareFiles, many);
        this.setState({ uploadFileErrorMessage });

        if (!uploadFileErrorMessage) {
            this.setState({
                files: {
                    ...files,
                    share: filteredFiles.map((it) => it.key),
                },
            });
        }
    };

    private handleUploadCloudStorageFiles = (cloudStorageFiles: RemoteFile[]): void => {
        const { files } = this.state;
        const { many } = this.props;
        const uploadFileErrorMessage = validateRemoteFiles(cloudStorageFiles, many);
        const filteredFiles = filterFiles(cloudStorageFiles, many);
        this.setState({ uploadFileErrorMessage });

        if (!uploadFileErrorMessage) {
            this.setState({
                files: {
                    ...files,
                    cloudStorage: filteredFiles.map((it) => it.key),
                },
            });
        }
    };

    private validateBlocks = (): Promise<any> =>
        new Promise((resolve, reject) => {
            const { projectId } = this.state;
            if (!this.validateLabelsOrProject()) {
                notification.error({
                    message: 'Could not create a task',
                    description: 'A task must contain at least one label or belong to some project',
                    className: 'cvat-notification-create-task-fail',
                });
                reject();
                return;
            }

            if (!this.validateFiles()) {
                notification.error({
                    message: 'Could not create a task',
                    description: 'A task must contain at least one file',
                    className: 'cvat-notification-create-task-fail',
                });
                reject();
                return;
            }

            if (!this.basicConfigurationComponent.current) {
                reject();
                return;
            }

            this.basicConfigurationComponent.current
                .submit()
                .then(() => {
                    if (this.advancedConfigurationComponent.current) {
                        return this.advancedConfigurationComponent.current.submit();
                    }
                    if (projectId) {
                        return core.projects
                            .get({ id: projectId })
                            .then((response: any) => {
                                const [project] = response;
                                const { advanced } = this.state;
                                this.handleSubmitAdvancedConfiguration({
                                    ...advanced,
                                    sourceStorage: new Storage(
                                        project.sourceStorage || { location: StorageLocation.LOCAL },
                                    ),
                                    targetStorage: new Storage(
                                        project.targetStorage || { location: StorageLocation.LOCAL },
                                    ),
                                });
                                return Promise.resolve();
                            })
                            .catch((error: Error): void => {
                                throw new Error(`Couldn't fetch the project ${projectId} ${error.toString()}`);
                            });
                    }
                    return Promise.resolve();
                })
                .then(resolve)
                .catch((error: Error | ValidateErrorEntity): void => {
                    notification.error({
                        message: 'Could not create a task',
                        description: (error as ValidateErrorEntity).errorFields
                            ? (error as ValidateErrorEntity).errorFields
                                .map((field) => `${field.name} : ${field.errors.join(';')}`)
                                .map((text: string): JSX.Element => <div>{text}</div>)
                            : error.toString(),
                        className: 'cvat-notification-create-task-fail',
                    });
                    reject(error);
                });
        });

    private handleSubmitAndOpen = (): void => {
        const { history } = this.props;

        this.validateBlocks()
            .then(this.createOneTask)
            .then((createdTask) => {
                this.monitorTaskTranscriptionStatus(createdTask.id, createdTask.name, {
                    notify: true,
                });
                const { id } = createdTask;
                history.push(`/tasks/${id}`);
            })
            .catch(() => { });
    };

    private handleSubmitAndContinue = (): void => {
        this.validateBlocks()
            .then(this.createOneTask)
            .then((createdTask) => {
                notification.info({
                    message: 'The task has been created',
                    className: 'cvat-notification-create-task-success',
                });
                this.resetState();
                this.focusToForm();
                this.monitorTaskTranscriptionStatus(createdTask.id, createdTask.name, {
                    updateSingleStatus: true,
                });
            })
            .catch(() => { });
    };

    private createOneTask = (): Promise<any> => {
        const { onCreate } = this.props;
        this.setState({
            singleTaskTranscriptionStatus: null,
        });
        this.startLoading();
        return onCreate(this.state, this.changeStatusInProgressTask).finally(this.stopLoading);
    };

    private setStatusOneOfMultiTasks = async (index: number, status: string): Promise<void> => {
        const { multiTasks } = this.state;
        const resultTask = {
            ...multiTasks[index],
            status,
        };

        return new Promise((resolve) => {
            const newMultiTasks: any = [...multiTasks.slice(0, index), resultTask, ...multiTasks.slice(index + 1)];
            this.setState(
                {
                    multiTasks: newMultiTasks,
                },
                resolve,
            );
        });
    };

    private createOneOfMultiTasks = async (index: any): Promise<void> => {
        const { onCreate } = this.props;
        const { multiTasks } = this.state;
        const task = multiTasks[index];

        if (task.status !== 'pending') return;

        await this.setStatusOneOfMultiTasks(index, 'progress');
        try {
            const createdTask = await onCreate(task);
            await this.setStatusOneOfMultiTasks(index, 'completed');
            this.updateMultiTaskTranscriptionStatus(index, {
                state: 'Queued',
                message: 'Transcription job is queued to start.',
                progress: 0,
            });
            this.monitorTaskTranscriptionStatus(createdTask.id, createdTask.name, {
                multiTaskIndex: index,
            });
        } catch (err) {
            console.warn(err);
            await this.setStatusOneOfMultiTasks(index, 'failed');
        }
    };

    private createMultiTasks = async (): Promise<any> => {
        const { multiTasks } = this.state;
        this.startLoading();
        const { length } = multiTasks;
        let index = 0;
        // Queue size controlls the number of tasks that are created parallelly.
        const queueSize = 3;
        const promises = Array(queueSize)
            .fill(undefined)
            .map(async (): Promise<void> => {
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    index++; // preliminary increase is needed to avoid using the same index when queueSize > 1
                    if (index > length) break;
                    await this.createOneOfMultiTasks(index - 1);
                }
            });
        await Promise.allSettled(promises);
        this.stopLoading();
    };

    private addMultiTasks = async (): Promise<void> =>
        new Promise((resolve) => {
            const {
                projectId,
                subset,
                advanced,
                labels,
                files: allFiles,
                activeFileManagerTab,
                cloudStorageId,
                jobAttrs,
            } = this.state;

            const files: (File | string)[] = allFiles[activeFileManagerTab];

            this.setState(
                {
                    multiTasks: files.map((file, index) => ({
                        projectId,
                        basic: {
                            name: this.getTaskName(index, activeFileManagerTab),
                        },
                        subset,
                        advanced,
                        labels,
                        files: {
                            ...defaultState.files,
                            [activeFileManagerTab]: [file],
                        },
                        activeFileManagerTab,
                        cloudStorageId,
                        status: 'pending',
                        transcriptionState: undefined,
                        transcriptionMessage: '',
                        transcriptionProgress: 0,
                        jobAttrs,
                    })),
                },
                resolve,
            );
        });

    private handleSubmitMultiTasks = (): void => {
        this.validateBlocks()
            .then(() => {
                this.addMultiTasks();
            })
            .then(this.createMultiTasks)
            .then(() => {
                const { multiTasks } = this.state;
                const countCompleted = multiTasks.filter((item) => item.status === 'completed').length;
                const countFailed = multiTasks.filter((item) => item.status === 'failed').length;
                const countCancelled = multiTasks.filter((item) => item.status === 'cancelled').length;
                const countAll = multiTasks.length;

                notification.info({
                    message: 'The tasks have been created',
                    description: `Completed: ${countCompleted}, failed: ${countFailed},${countCancelled ? ` cancelled: ${countCancelled},` : ''
                        } total: ${countAll}, `,
                    className: 'cvat-notification-create-task-success',
                });
            });
    };

    private handleCancelMultiTasks = (): void => {
        const { multiTasks } = this.state;
        let count = 0;
        const newMultiTasks: any = multiTasks.map((it) => {
            if (it.status === 'pending') {
                count++;
                return {
                    ...it,
                    status: 'cancelled',
                };
            }
            return it;
        });
        this.setState(
            {
                multiTasks: newMultiTasks,
            },
            () => {
                notification.info({
                    message: `Creation of ${count} tasks have been canceled`,
                    className: 'cvat-notification-create-task-success',
                });
            },
        );
    };

    private handleOkMultiTasks = (): void => {
        const { history, projectId } = this.props;
        if (projectId) {
            history.push(`/projects/${projectId}`);
        } else {
            history.push('/tasks/');
        }
    };

    private handleRetryCancelledMultiTasks = (): void => {
        const { multiTasks } = this.state;
        const newMultiTasks: any = multiTasks.map((it) => {
            if (it.status === 'cancelled') {
                return {
                    ...it,
                    status: 'pending',
                };
            }
            return it;
        });
        this.setState(
            {
                multiTasks: newMultiTasks,
            },
            () => {
                this.createMultiTasks();
            },
        );
    };

    private handleRetryFailedMultiTasks = (): void => {
        const { multiTasks } = this.state;
        const newMultiTasks: any = multiTasks.map((it) => {
            if (it.status === 'failed') {
                return {
                    ...it,
                    status: 'pending',
                };
            }
            return it;
        });
        this.setState(
            {
                multiTasks: newMultiTasks,
            },
            () => {
                this.createMultiTasks();
            },
        );
    };

    private getTaskName = (indexFile: number, fileManagerTabName: TabName, defaultFileName = ''): string => {
        const { many } = this.props;
        const { basic } = this.state;
        const { files } = this.state;
        const file = files[fileManagerTabName][indexFile];
        let fileName = defaultFileName;
        switch (fileManagerTabName) {
            case 'remote':
                fileName = getFileNameFromPath(file as string) || defaultFileName;
                break;
            case 'share':
                fileName = getFileNameFromPath(file as string) || defaultFileName;
                break;
            default:
                fileName = (file as File)?.name || (file as string) || defaultFileName;
                break;
        }
        return many
            ? basic.name.replaceAll('{{file_name}}', fileName).replaceAll('{{index}}', indexFile.toString())
            : basic.name;
    };

    private renderBasicBlock(): JSX.Element {
        const { many } = this.props;
        const exampleMultiTaskName = many ? this.getTaskName(0, 'local', 'fileName.mp4') : '';

        return (
            <Col span={24}>
                <BasicConfigurationForm
                    ref={this.basicConfigurationComponent}
                    many={many}
                    exampleMultiTaskName={exampleMultiTaskName}
                    onChange={this.handleChangeBasicConfiguration}
                />
            </Col>
        );
    }

    private renderProjectBlock(): JSX.Element {
        const { projectId } = this.state;

        return (
            <div style={{ display: 'none' }}>
                <Col span={24}>
                    <Text className='cvat-text-color'>Project</Text>
                </Col>
                <Col span={24}>
                    <ProjectSearchField onSelect={this.handleProjectIdChange} value={projectId} />
                </Col>
            </div>
        );
    }

    private renderSubsetBlock(): JSX.Element | null {
        const { projectId, subset } = this.state;

        if (projectId !== null) {
            return (
                <>
                    <Col span={24}>
                        <Text className='cvat-text-color'>Subset</Text>
                    </Col>
                    <Col span={24}>
                        <ProjectSubsetField
                            value={subset}
                            onChange={this.handleTaskSubsetChange}
                            projectId={projectId}
                        />
                    </Col>
                </>
            );
        }

        return null;
    }

    private renderLabelsBlock(): JSX.Element {
        const { projectId, labels } = this.state;

        if (projectId) {
            return (
                <>
                    <Col span={24}>
                        <Text className='cvat-text-color'>Labels</Text>
                    </Col>
                    <Col span={24}>
                        <Text type='secondary'>Project labels will be used</Text>
                    </Col>
                </>
            );
        }

        return (
            <Col span={24}>
                <Text type='danger'>* </Text>
                <Text className='cvat-text-color'>Labels</Text>
                <LabelsEditor
                    labels={labels}
                    onSubmit={(newLabels): void => {
                        this.setState({
                            labels: newLabels,
                        });
                    }}
                />
            </Col>
        );
    }

    private renderFilesBlock(): JSX.Element {
        const { many } = this.props;
        const { uploadFileErrorMessage } = this.state;

        return (
            <>
                <Col span={24}>
                    <Text type='danger'>* </Text>
                    <Text className='cvat-text-color'>Select files</Text>
                    <FileManagerComponent
                        many={many}
                        onChangeActiveKey={this.changeFileManagerTab}
                        onUploadLocalFiles={this.handleUploadLocalFiles}
                        onUploadRemoteFiles={this.handleUploadRemoteFiles}
                        onUploadShareFiles={this.handleUploadShareFiles}
                        onUploadCloudStorageFiles={this.handleUploadCloudStorageFiles}
                        ref={(component): void => {
                            this.fileManagerComponent = component;
                        }}
                    />
                </Col>
                {uploadFileErrorMessage ? (
                    <Col span={24}>
                        <Alert
                            className='cvat-create-task-content-alert'
                            type='error'
                            message={uploadFileErrorMessage}
                            showIcon
                        />
                    </Col>
                ) : null}
            </>
        );
    }

    private renderAdvancedBlock(): JSX.Element {
        const { installedGit, dumpers } = this.props;
        const { activeFileManagerTab, projectId } = this.state;

        const {
            advanced: {
                useProjectSourceStorage,
                useProjectTargetStorage,
                sourceStorage: { location: sourceStorageLocation },
                targetStorage: { location: targetStorageLocation },
            },
        } = this.state;
        return (
            <Col span={24}>
                <Collapse className='cvat-advanced-configuration-wrapper'>
                    <Collapse.Panel key='1' header={<Text className='cvat-title'>Advanced configuration</Text>}>
                        <AdvancedConfigurationForm
                            dumpers={dumpers}
                            installedGit={installedGit}
                            activeFileManagerTab={activeFileManagerTab}
                            ref={this.advancedConfigurationComponent}
                            onSubmit={this.handleSubmitAdvancedConfiguration}
                            projectId={projectId}
                            useProjectSourceStorage={useProjectSourceStorage}
                            useProjectTargetStorage={useProjectTargetStorage}
                            sourceStorageLocation={sourceStorageLocation}
                            targetStorageLocation={targetStorageLocation}
                            onChangeUseProjectSourceStorage={this.handleUseProjectSourceStorageChange}
                            onChangeUseProjectTargetStorage={this.handleUseProjectTargetStorageChange}
                            onChangeSourceStorageLocation={(value: StorageLocation) => {
                                this.handleChangeStorageLocation('sourceStorage', value);
                            }}
                            onChangeTargetStorageLocation={(value: StorageLocation) => {
                                this.handleChangeStorageLocation('targetStorage', value);
                            }}
                        />
                    </Collapse.Panel>
                </Collapse>
            </Col>
        );
    }

    private renderFooterSingleTask(): JSX.Element {
        const {
            uploadFileErrorMessage,
            loading,
            statusInProgressTask: status,
            singleTaskTranscriptionStatus,
        } = this.state;

        const showStatus = loading || Boolean(status);
        const alertType = status.toLowerCase().includes('failed') ? 'error' : 'info';
        const alertMessage = loading ? (status || 'Task is being created...') : status;
        const transcriptionStateLabel = singleTaskTranscriptionStatus ? ({
            Queued: 'Queued',
            Started: 'In progress',
            Finished: 'Completed',
            Failed: 'Failed',
        }[singleTaskTranscriptionStatus.state]) : null;
        const transcriptionPercent = singleTaskTranscriptionStatus ? (() => {
            if (singleTaskTranscriptionStatus.state === 'Finished' || singleTaskTranscriptionStatus.state === 'Failed') {
                return 100;
            }

            if (singleTaskTranscriptionStatus.state === 'Started') {
                const progress = Number.isFinite(singleTaskTranscriptionStatus.progress) ?
                    Math.round(singleTaskTranscriptionStatus.progress as number) :
                    0;
                return Math.max(progress, 5);
            }

            return 0;
        })() : 0;
        const transcriptionProgressStatus = singleTaskTranscriptionStatus ? ({
            Queued: 'active',
            Started: 'active',
            Finished: 'success',
            Failed: 'exception',
        }[singleTaskTranscriptionStatus.state] as 'active' | 'success' | 'exception') : 'active';

        return (
            <>
                {showStatus ? (
                    <Row>
                        <Col span={24}>
                            <Alert
                                type={alertType}
                                message={alertMessage}
                                showIcon
                                style={{ marginBottom: 8 }}
                            />
                        </Col>
                    </Row>
                ) : null}
                {singleTaskTranscriptionStatus ? (
                    <Row>
                        <Col span={24}>
                            <Alert
                                type={singleTaskTranscriptionStatus.state === 'Failed' ? 'error' : 'info'}
                                message={`Transcription: ${transcriptionStateLabel}`}
                                description={singleTaskTranscriptionStatus.message || 'Transcription status is being updated.'}
                                showIcon
                                style={{ marginBottom: 8 }}
                            />
                            <Progress
                                status={transcriptionProgressStatus}
                                percent={transcriptionPercent}
                                strokeWidth={5}
                                size='small'
                                showInfo={false}
                            />
                        </Col>
                    </Row>
                ) : null}
                <Row justify='end' gutter={5}>
                    <Col>
                        <Button
                            className='cvat-submit-open-task-button'
                            type='primary'
                            onClick={this.handleSubmitAndOpen}
                            disabled={!!uploadFileErrorMessage || loading}
                        >
                            Submit & Open
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            className='cvat-submit-open-task-button'
                            type='primary'
                            onClick={this.handleSubmitAndContinue}
                            disabled={!!uploadFileErrorMessage || loading}
                        >
                            Submit & Continue
                        </Button>
                    </Col>
                </Row>
            </>
        );
    }

    private renderFooterMultiTasks(): JSX.Element {
        const { multiTasks: items, uploadFileErrorMessage, files, activeFileManagerTab, loading } = this.state;
        const currentFiles = files[activeFileManagerTab];
        const countPending = items.filter((item) => item.status === 'pending').length;
        const countAll = items.length;

        if ((loading || countPending !== countAll) && currentFiles.length) {
            return (
                <MultiTasksProgress
                    tasks={items}
                    onOk={this.handleOkMultiTasks}
                    onCancel={this.handleCancelMultiTasks}
                    onRetryFailedTasks={this.handleRetryFailedMultiTasks}
                    onRetryCancelledTasks={this.handleRetryCancelledMultiTasks}
                />
            );
        }

        return (
            <Row justify='end' gutter={5}>
                <Col>
                    <Button
                        className='cvat-submit-multiple-tasks-button'
                        htmlType='submit'
                        type='primary'
                        onClick={this.handleSubmitMultiTasks}
                        disabled={!!uploadFileErrorMessage}
                    >
                        Submit&nbsp;
                        {currentFiles.length}
                        &nbsp;tasks
                    </Button>
                </Col>
            </Row>
        );
    }

    public render(): JSX.Element {
        const { many } = this.props;

        return (
            <Row justify='start' align='middle' className='cvat-create-task-content'>
                <Col span={24}>
                    <Text className='cvat-title'>Basic configuration</Text>
                </Col>

                {this.renderBasicBlock()}
                {this.renderProjectBlock()}
                {this.renderSubsetBlock()}
                {this.renderLabelsBlock()}
                {this.renderFilesBlock()}
                {this.renderAdvancedBlock()}

                <Col span={24} className='cvat-create-task-content-footer'>
                    {many ? this.renderFooterMultiTasks() : this.renderFooterSingleTask()}
                </Col>
            </Row>
        );
    }
}

export default withRouter(CreateTaskContent);
